import { useState, useEffect, useRef, useCallback, useMemo, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { extractBrandName, getWorkspaceBrandName } from '../../lib/brandName';
import { getLlmMeta } from '../../lib/llmModels';
import { mergeResponseLinks, parseProviderLinks } from '../../lib/providerLinks';
import { InfoTooltip } from '../../components/InfoTooltip';
import { SearchResponseModal, type SearchModalMentionStatus, type SearchResponseModalItem } from '../../components/SearchResponseModal';
import { useAuthStore } from '../../stores/authStore';
import { useToast } from '../../components/Toast';
import type {
  Competitor,
  PromptResponse,
  PromptResponseCitation,
  PromptResponseMention,
  PromptRun,
  TrackedPrompt,
} from '../../types/database';

type PresetKey = 'today' | 'last7' | 'last30' | 'last90' | 'last365' | 'custom' | 'all';
type BrandChartMode = 'brand' | 'timeline';
type PromptResponseRow = Pick<
  PromptResponse,
  | 'id'
  | 'prompt_id'
  | 'run_id'
  | 'model'
  | 'brand_mentioned'
  | 'brand_sentiment'
  | 'brand_position'
  | 'response_text'
  | 'competitors_mentioned'
  | 'citations'
  | 'provider_links'
  | 'created_at'
>;

type LegacyCompetitorMentionEntry = {
  domain?: unknown;
  mentioned?: unknown;
};

type MentionRow = Pick<
  PromptResponseMention,
  | 'prompt_response_id'
  | 'prompt_id'
  | 'run_id'
  | 'model'
  | 'brand_name'
  | 'brand_domain'
  | 'mentioned'
  | 'position'
  | 'mention_count'
  | 'sentiment'
  | 'created_at'
>;

type CitationRow = Pick<
  PromptResponseCitation,
  | 'prompt_response_id'
  | 'prompt_id'
  | 'run_id'
  | 'model'
  | 'citation_url'
  | 'citation_domain'
  | 'source_type'
  | 'associated_brand_domains'
  | 'created_at'
>;

type BrandEntity = {
  name: string;
  domain: string;
  faviconUrl: string;
  isWorkspaceBrand: boolean;
};

type PromptRunRow = Pick<PromptRun, 'id' | 'status' | 'completed_at' | 'created_at'>;

type SourceListRow = {
  key: string;
  label: string;
  sublabel: string;
  count: number;
  kind: 'website' | 'llm';
  faviconUrl?: string;
  color?: string;
  icon?: string;
  shortLabel?: string;
};

type MetricTrend = {
  status: 'positive' | 'negative' | 'neutral';
  deltaPercent: number | null;
  tooltip: string;
};

type MetricValueFormatter = (value: number) => string;

type HoveredTimelinePoint = {
  domain: string;
  name: string;
  total: number;
  visibility: number | null;
  xPercent: number;
  yPercent: number;
};

type PortalTooltipPosition = {
  left: number;
  top: number;
  placement: 'top' | 'bottom';
};

const PRESETS: { key: PresetKey; label: string }[] = [
  { key: 'today', label: 'Today' },
  { key: 'last7', label: 'Last 7 days' },
  { key: 'last30', label: 'Last 30 days' },
  { key: 'last90', label: 'Last 3 months' },
  { key: 'last365', label: 'Last 12 months' },
  { key: 'custom', label: 'Custom' },
  { key: 'all', label: 'All time' },
];

const BRAND_COLORS = ['#ef4444', '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899'];
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAY_HEADERS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

function RankingVisibilityTooltip({
  text,
  children,
}: {
  text: string;
  children: ReactNode;
}) {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLSpanElement>(null);
  const [isActive, setIsActive] = useState(false);
  const [position, setPosition] = useState<PortalTooltipPosition | null>(null);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    const tooltip = tooltipRef.current;

    if (!trigger || !tooltip) return;

    const triggerRect = trigger.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();
    const viewportPadding = 8;
    const tooltipGap = 10;
    const preferredTop = triggerRect.top - tooltipRect.height - tooltipGap;
    const shouldPlaceBelow = preferredTop < viewportPadding;
    const placement = shouldPlaceBelow ? 'bottom' : 'top';
    const top = shouldPlaceBelow
      ? triggerRect.bottom + tooltipGap
      : triggerRect.top - tooltipRect.height - tooltipGap;
    const centeredLeft = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
    const maxLeft = window.innerWidth - tooltipRect.width - viewportPadding;
    const left = Math.min(Math.max(centeredLeft, viewportPadding), Math.max(viewportPadding, maxLeft));

    setPosition({
      left,
      top: Math.max(viewportPadding, top),
      placement,
    });
  }, []);

  useEffect(() => {
    if (!isActive) {
      setPosition(null);
      return;
    }

    const frame = window.requestAnimationFrame(updatePosition);
    const handleViewportChange = () => updatePosition();

    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
    };
  }, [isActive, updatePosition]);

  return (
    <>
      <span
        ref={triggerRef}
        className="overview-ranking-visibility-tooltip"
        onMouseEnter={() => setIsActive(true)}
        onMouseLeave={() => setIsActive(false)}
        onFocus={() => setIsActive(true)}
        onBlur={() => setIsActive(false)}
      >
        {children}
      </span>
      {isActive && typeof document !== 'undefined'
        ? createPortal(
            <span
              ref={tooltipRef}
              className={`overview-portal-tooltip is-${position?.placement || 'top'} ${position ? 'is-visible' : ''}`}
              style={{
                left: position?.left ?? -9999,
                top: position?.top ?? -9999,
              }}
            >
              {text}
            </span>,
            document.body
          )
        : null}
    </>
  );
}

const normalizeDomain = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/+$/, '')
    .split('/')[0];

const domainMatches = (inputDomain: string, targetDomain: string): boolean => {
  const input = normalizeDomain(inputDomain);
  const target = normalizeDomain(targetDomain);
  return input === target || input.endsWith(`.${target}`);
};

const toStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string');
};

const isWithinDateRange = (
  value: string,
  rangeStart: Date | null,
  rangeEnd: Date | null
): boolean => {
  const timestamp = new Date(value).getTime();
  if (Number.isNaN(timestamp)) return false;

  if (rangeStart) {
    const start = new Date(rangeStart.getFullYear(), rangeStart.getMonth(), rangeStart.getDate()).getTime();
    if (timestamp < start) return false;
  }

  if (rangeEnd) {
    const end = new Date(rangeEnd.getFullYear(), rangeEnd.getMonth(), rangeEnd.getDate(), 23, 59, 59, 999).getTime();
    if (timestamp > end) return false;
  }

  return true;
};

const startOfDay = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const endOfDay = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);

const formatDateShort = (date: Date) =>
  `${date.getDate()} ${MONTH_NAMES[date.getMonth()].slice(0, 3)} ${date.getFullYear()}`;

const formatDate = (value: string): string =>
  new Date(value).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

const sentimentToScore = (value: string | null | undefined): number | null => {
  if (value === 'positive') return 100;
  if (value === 'neutral') return 50;
  if (value === 'negative') return 0;
  return null;
};

const describeSentiment = (score: number | null): string => {
  if (score === null) return 'No data yet';
  if (score >= 70) return 'Positive leaning';
  if (score <= 30) return 'Negative leaning';
  return 'Neutral leaning';
};

const average = (values: number[]): number | null => {
  if (values.length === 0) return null;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
};

const getPromptModelKey = (response: Pick<PromptResponseRow, 'id' | 'prompt_id' | 'model'>) =>
  `${response.prompt_id ?? `deleted-${response.id}`}::${response.model}`;

const buildEffectiveRangeData = (
  responseRows: PromptResponseRow[],
  mentionRows: MentionRow[],
  citationRows: CitationRow[]
) => {
  const latestResponsesByPromptModel = new Map<string, PromptResponseRow>();

  responseRows.forEach((response) => {
    const key = getPromptModelKey(response);
    const existing = latestResponsesByPromptModel.get(key);

    if (!existing || new Date(response.created_at).getTime() > new Date(existing.created_at).getTime()) {
      latestResponsesByPromptModel.set(key, response);
    }
  });

  const responses = Array.from(latestResponsesByPromptModel.values());
  const responseIds = new Set(responses.map((response) => response.id));

  return {
    responses,
    mentions: mentionRows.filter((mention) => responseIds.has(mention.prompt_response_id)),
    citations: citationRows.filter((citation) => responseIds.has(citation.prompt_response_id)),
  };
};

const buildMetricTrend = ({
  label,
  current,
  previous,
  higherIsBetter = true,
  currentPeriodDescription,
  previousPeriodDescription,
  formatValue = (value) => value.toLocaleString(undefined, { maximumFractionDigits: 1 }),
}: {
  label: string;
  current: number | null;
  previous: number | null;
  higherIsBetter?: boolean;
  currentPeriodDescription: string;
  previousPeriodDescription: string | null;
  formatValue?: MetricValueFormatter;
}): MetricTrend => {
  if (current === null || previous === null || !previousPeriodDescription) {
    return {
      status: 'neutral',
      deltaPercent: null,
      tooltip: 'No previous period comparison is available yet.',
    };
  }

  const rawDelta = current - previous;
  const adjustedDelta = higherIsBetter ? rawDelta : -rawDelta;
  const status = adjustedDelta > 0 ? 'positive' : adjustedDelta < 0 ? 'negative' : 'neutral';
  const safeBase = previous === 0 ? (current === 0 ? 1 : Math.abs(current)) : Math.abs(previous);
  const deltaPercent = safeBase === 0 ? 0 : Math.round((Math.abs(rawDelta) / safeBase) * 100);

  return {
    status,
    deltaPercent,
    tooltip: `${label} changed from ${formatValue(previous)} in ${previousPeriodDescription} to ${formatValue(current)} in ${currentPeriodDescription}.`,
  };
};

const getSelectedPeriodDayCount = (rangeStart: Date | null, rangeEnd: Date | null): number | null => {
  if (!rangeStart || !rangeEnd) return null;
  const start = startOfDay(rangeStart).getTime();
  const end = startOfDay(rangeEnd).getTime();
  return Math.max(1, Math.round((end - start) / (24 * 60 * 60 * 1000)) + 1);
};

const getCurrentPeriodDescription = (preset: PresetKey, dayCount: number | null): string => {
  if (preset === 'today') return 'today';
  if (!dayCount) return 'the selected period';
  return dayCount === 1 ? 'the last day' : `the last ${dayCount} days`;
};

const getPreviousPeriodDescription = (preset: PresetKey, dayCount: number | null): string | null => {
  if (preset === 'today') return 'previous day period';
  if (!dayCount) return null;
  return dayCount === 1 ? 'previous day period' : `previous ${dayCount} day period`;
};

const citationMatchesBrand = (citation: CitationRow, brandDomain: string): boolean => {
  const citationDomain = normalizeDomain(citation.citation_domain || citation.citation_url || '');
  if (citationDomain && domainMatches(citationDomain, brandDomain)) return true;

  return toStringArray(citation.associated_brand_domains)
    .map(normalizeDomain)
    .some((domain) => domainMatches(domain, brandDomain));
};

const citationIsDirectForBrand = (citation: CitationRow, brandDomain: string): boolean =>
  domainMatches(citation.citation_domain || citation.citation_url || '', brandDomain);

const computePreviousRange = (
  rangeStart: Date | null,
  rangeEnd: Date | null
): { start: Date; end: Date } | null => {
  if (!rangeStart || !rangeEnd) return null;

  const currentStart = startOfDay(rangeStart).getTime();
  const currentEnd = endOfDay(rangeEnd).getTime();
  const duration = currentEnd - currentStart + 1;
  const previousEnd = new Date(currentStart - 1);
  const previousStart = new Date(previousEnd.getTime() - duration + 1);

  return {
    start: previousStart,
    end: previousEnd,
  };
};

const getDaysInMonth = (year: number, month: number) =>
  new Date(year, month + 1, 0).getDate();

const getFirstDayOfWeek = (year: number, month: number) => {
  const day = new Date(year, month, 1).getDay();
  return day === 0 ? 6 : day - 1;
};

const isSameDay = (a: Date | null, b: Date | null) => {
  if (!a || !b) return false;
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
};

const isBetween = (date: Date, start: Date | null, end: Date | null) => {
  if (!start || !end) return false;
  const t = date.getTime();
  return t > start.getTime() && t < end.getTime();
};

const formatTimelineLabel = (date: Date) =>
  date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

const getTimelineTickIndices = (length: number, desiredTicks = 6): number[] => {
  if (length <= 0) return [];
  if (length === 1) return [0];

  const maxTicks = Math.min(length, desiredTicks);
  const indices = new Set<number>([0, length - 1]);

  for (let i = 1; i < maxTicks - 1; i += 1) {
    const index = Math.round((i * (length - 1)) / (maxTicks - 1));
    indices.add(index);
  }

  return Array.from(indices).sort((a, b) => a - b);
};

const TIMELINE_VIEWBOX_WIDTH = 1000;
const TIMELINE_VIEWBOX_HEIGHT = 240;
const TIMELINE_PLOT_INSET_X = 14;
const TIMELINE_PLOT_INSET_TOP = 10;
const TIMELINE_PLOT_INSET_BOTTOM = 6;

const getTimelinePointX = (index: number, totalPoints: number) =>
  totalPoints <= 1
    ? TIMELINE_VIEWBOX_WIDTH / 2
    : TIMELINE_PLOT_INSET_X
      + (index / (totalPoints - 1)) * (TIMELINE_VIEWBOX_WIDTH - TIMELINE_PLOT_INSET_X * 2);

const getTimelinePointY = (value: number, maxValue: number) =>
  maxValue <= 0
    ? TIMELINE_VIEWBOX_HEIGHT - TIMELINE_PLOT_INSET_BOTTOM
    : TIMELINE_PLOT_INSET_TOP
      + (1 - value / maxValue) * (TIMELINE_VIEWBOX_HEIGHT - TIMELINE_PLOT_INSET_TOP - TIMELINE_PLOT_INSET_BOTTOM);

const buildTimelinePath = (values: number[], maxValue: number) =>
  values
    .map((value, index) => {
      const x = getTimelinePointX(index, values.length);
      const y = getTimelinePointY(value, maxValue);
      return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
    })
    .join(' ');

const buildTimelineAreaPath = (values: number[], maxValue: number) => {
  if (values.length === 0) return '';

  const bottomY = TIMELINE_VIEWBOX_HEIGHT - TIMELINE_PLOT_INSET_BOTTOM;
  const firstX = getTimelinePointX(0, values.length);
  const lastX = getTimelinePointX(values.length - 1, values.length);
  const linePath = buildTimelinePath(values, maxValue);

  return `M ${firstX} ${bottomY} ${linePath.slice(1)} L ${lastX} ${bottomY} Z`;
};

function Calendar({
  year,
  month,
  rangeStart,
  rangeEnd,
  onDayClick,
  onPrevMonth,
  onNextMonth,
  showPrevNav,
  showNextNav,
}: {
  year: number;
  month: number;
  rangeStart: Date | null;
  rangeEnd: Date | null;
  onDayClick: (date: Date) => void;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  showPrevNav: boolean;
  showNextNav: boolean;
}) {
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfWeek(year, month);
  const today = new Date();

  const prevMonth = month === 0 ? 11 : month - 1;
  const prevYear = month === 0 ? year - 1 : year;
  const daysInPrevMonth = getDaysInMonth(prevYear, prevMonth);

  const cells: { day: number; isOtherMonth: boolean; date: Date }[] = [];

  for (let i = firstDay - 1; i >= 0; i--) {
    const day = daysInPrevMonth - i;
    cells.push({ day, isOtherMonth: true, date: new Date(prevYear, prevMonth, day) });
  }

  for (let day = 1; day <= daysInMonth; day++) {
    cells.push({ day, isOtherMonth: false, date: new Date(year, month, day) });
  }

  const remaining = 7 - (cells.length % 7);
  if (remaining < 7) {
    const nextMonth = month === 11 ? 0 : month + 1;
    const nextYear = month === 11 ? year + 1 : year;
    for (let day = 1; day <= remaining; day++) {
      cells.push({ day, isOtherMonth: true, date: new Date(nextYear, nextMonth, day) });
    }
  }

  return (
    <div className="date-range-calendar">
      <div className="calendar-header">
        <button
          className={`calendar-nav-btn ${!showPrevNav ? 'is-hidden' : ''}`}
          onClick={onPrevMonth}
          type="button"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m15 18-6-6 6-6" />
          </svg>
        </button>
        <div className="calendar-month-title">
          {MONTH_NAMES[month]} {year}
        </div>
        <button
          className={`calendar-nav-btn ${!showNextNav ? 'is-hidden' : ''}`}
          onClick={onNextMonth}
          type="button"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m9 18 6-6-6-6" />
          </svg>
        </button>
      </div>
      <div className="calendar-grid">
        {DAY_HEADERS.map((day) => (
          <div key={day} className="calendar-day-header">
            {day}
          </div>
        ))}
        {cells.map((cell, index) => {
          const isSelected = isSameDay(cell.date, rangeStart) || isSameDay(cell.date, rangeEnd);
          const isInRange = isBetween(cell.date, rangeStart, rangeEnd);
          const isToday = isSameDay(cell.date, today) && !cell.isOtherMonth;
          const isFuture = cell.date > today;

          let className = 'calendar-day';
          if (cell.isOtherMonth) className += ' is-other-month';
          if (isFuture) className += ' is-disabled';
          if (isSelected) className += ' is-selected';
          if (isInRange && !cell.isOtherMonth) className += ' is-in-range';
          if (isSelected && isSameDay(cell.date, rangeStart) && rangeEnd) className += ' is-range-start';
          if (isSelected && isSameDay(cell.date, rangeEnd) && rangeStart) className += ' is-range-end';
          if (isToday) className += ' is-today';

          return (
            <button
              key={index}
              className={className}
              onClick={() => !isFuture && !cell.isOtherMonth && onDayClick(cell.date)}
              type="button"
              disabled={isFuture}
            >
              {cell.day}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function Overview() {
  const { workspace } = useAuthStore();
  const toast = useToast();

  const [activePreset, setActivePreset] = useState<PresetKey>('today');
  const [appliedPreset, setAppliedPreset] = useState<PresetKey>('today');
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [datePickerVisible, setDatePickerVisible] = useState(false);
  const [isBrandDropdownOpen, setIsBrandDropdownOpen] = useState(false);
  const [brandChartMode, setBrandChartMode] = useState<BrandChartMode>('brand');
  const [activeTimelineDomain, setActiveTimelineDomain] = useState<string | null>(null);
  const [hiddenTimelineDomains, setHiddenTimelineDomains] = useState<string[]>([]);
  const [hoveredTimelinePoint, setHoveredTimelinePoint] = useState<HoveredTimelinePoint | null>(null);
  const [selectedRecentSearchId, setSelectedRecentSearchId] = useState<string | null>(null);
  const [selectedBrandDomain, setSelectedBrandDomain] = useState('');
  const datePickerRef = useRef<HTMLDivElement>(null);
  const brandDropdownRef = useRef<HTMLDivElement>(null);

  const today = new Date();
  const [rangeStart, setRangeStart] = useState<Date | null>(
    new Date(today.getTime() - 24 * 60 * 60 * 1000)
  );
  const [rangeEnd, setRangeEnd] = useState<Date | null>(today);

  const [tempStart, setTempStart] = useState<Date | null>(rangeStart);
  const [tempEnd, setTempEnd] = useState<Date | null>(rangeEnd);
  const [pickingStart, setPickingStart] = useState(true);

  const [leftMonth, setLeftMonth] = useState(today.getMonth() === 0 ? 11 : today.getMonth() - 1);
  const [leftYear, setLeftYear] = useState(today.getMonth() === 0 ? today.getFullYear() - 1 : today.getFullYear());

  const rightMonth = leftMonth === 11 ? 0 : leftMonth + 1;
  const rightYear = leftMonth === 11 ? leftYear + 1 : leftYear;

  useEffect(() => {
    const message = sessionStorage.getItem('auth_message');
    if (message) {
      sessionStorage.removeItem('auth_message');
      toast.success(message);
    }
  }, [toast]);

  const openDatePicker = useCallback(() => {
    setTempStart(rangeStart);
    setTempEnd(rangeEnd);
    setActivePreset(appliedPreset);
    setPickingStart(true);
    setDatePickerVisible(true);
    requestAnimationFrame(() => {
      setIsDatePickerOpen(true);
    });
  }, [appliedPreset, rangeStart, rangeEnd]);

  const closeDatePicker = useCallback(() => {
    setIsDatePickerOpen(false);
    setTimeout(() => setDatePickerVisible(false), 120);
  }, []);

  const dismissDatePicker = useCallback(() => {
    setTempStart(rangeStart);
    setTempEnd(rangeEnd);
    setActivePreset(appliedPreset);
    setPickingStart(true);
    closeDatePicker();
  }, [appliedPreset, closeDatePicker, rangeEnd, rangeStart]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
        dismissDatePicker();
      }
    };

    if (datePickerVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [datePickerVisible, dismissDatePicker]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (brandDropdownRef.current && !brandDropdownRef.current.contains(event.target as Node)) {
        setIsBrandDropdownOpen(false);
      }
    };

    if (isBrandDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isBrandDropdownOpen]);

  const handlePresetClick = (key: PresetKey) => {
    setActivePreset(key);
    const now = new Date();
    let start: Date | null = null;
    let end: Date | null = now;

    if (key === 'today') start = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    else if (key === 'last7') start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6);
    else if (key === 'last30') start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 27);
    else if (key === 'last90') start = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
    else if (key === 'last365') start = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
    else if (key === 'all') {
      start = null;
      end = null;
    } else if (key === 'custom') {
      return;
    }

    setTempStart(start);
    setTempEnd(end);
    setRangeStart(start);
    setRangeEnd(end);
    setAppliedPreset(key);
    closeDatePicker();
  };

  const handleDayClick = (date: Date) => {
    setActivePreset('custom');
    if (pickingStart) {
      setTempStart(date);
      setTempEnd(null);
      setPickingStart(false);
    } else {
      if (tempStart && date < tempStart) {
        setTempEnd(tempStart);
        setTempStart(date);
      } else {
        setTempEnd(date);
      }
      setPickingStart(true);
    }
  };

  const handleApply = () => {
    setAppliedPreset(activePreset);
    setRangeStart(tempStart);
    setRangeEnd(tempEnd);
    closeDatePicker();
  };

  const handleCancel = () => dismissDatePicker();

  const handlePrevMonth = () => {
    if (leftMonth === 0) {
      setLeftMonth(11);
      setLeftYear(leftYear - 1);
    } else {
      setLeftMonth(leftMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (leftMonth === 11) {
      setLeftMonth(0);
      setLeftYear(leftYear + 1);
    } else {
      setLeftMonth(leftMonth + 1);
    }
  };

  const dateLabel = (() => {
    const applied = PRESETS.find((item) => item.key === appliedPreset);
    if (appliedPreset !== 'custom') return applied?.label || 'Today';
    if (rangeStart && rangeEnd) return `${formatDateShort(rangeStart)} – ${formatDateShort(rangeEnd)}`;
    return 'Custom';
  })();

  const { data: competitors = [] } = useQuery({
    queryKey: ['competitors', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return [];
      const { data, error } = await supabase
        .from('competitors')
        .select('*')
        .eq('workspace_id', workspace.id)
        .order('created_at', { ascending: true });

      if (error) throw error;
      return data as Competitor[];
    },
    enabled: !!workspace?.id,
  });

  const { data: prompts = [] } = useQuery({
    queryKey: ['tracked_prompts', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return [];
      const { data, error } = await supabase
        .from('tracked_prompts')
        .select('*')
        .eq('workspace_id', workspace.id)
        .order('created_at', { ascending: true });

      if (error) throw error;
      return data as TrackedPrompt[];
    },
    enabled: !!workspace?.id,
  });

  const { data: responses = [] } = useQuery({
    queryKey: ['overview_prompt_responses', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return [];
      const { data, error } = await supabase
        .from('prompt_responses')
        .select('id, prompt_id, run_id, model, brand_mentioned, brand_sentiment, brand_position, response_text, competitors_mentioned, citations, provider_links, created_at')
        .eq('workspace_id', workspace.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as PromptResponseRow[];
    },
    enabled: !!workspace?.id,
  });

  const { data: mentions = [] } = useQuery({
    queryKey: ['overview_prompt_response_mentions', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return [];
      const { data, error } = await supabase
        .from('prompt_response_mentions')
        .select(
          'prompt_response_id, prompt_id, run_id, model, brand_name, brand_domain, mentioned, position, mention_count, sentiment, created_at'
        )
        .eq('workspace_id', workspace.id)
        .eq('mentioned', true)
        .order('created_at', { ascending: false });

      if (error) {
        if (error.code === '42P01') return [];
        throw error;
      }

      return data as MentionRow[];
    },
    enabled: !!workspace?.id,
  });

  const { data: citations = [] } = useQuery({
    queryKey: ['overview_prompt_response_citations', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return [];
      const { data, error } = await supabase
        .from('prompt_response_citations')
        .select(
          'prompt_response_id, prompt_id, run_id, model, citation_url, citation_domain, source_type, associated_brand_domains, created_at'
        )
        .eq('workspace_id', workspace.id)
        .order('created_at', { ascending: false });

      if (error) {
        if (error.code === '42P01') return [];
        throw error;
      }

      return data as CitationRow[];
    },
    enabled: !!workspace?.id,
  });

  const { data: promptRuns = [] } = useQuery({
    queryKey: ['overview_prompt_runs', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return [];
      const { data, error } = await supabase
        .from('prompt_runs')
        .select('id, status, completed_at, created_at')
        .eq('workspace_id', workspace.id)
        .eq('status', 'completed')
        .order('completed_at', { ascending: false });

      if (error) throw error;
      return data as PromptRunRow[];
    },
    enabled: !!workspace?.id,
  });

  const workspaceBrandDomain = workspace?.brand_url ? normalizeDomain(workspace.brand_url) : '';
  const workspaceBrandName = getWorkspaceBrandName(workspace);

  const brandEntities = useMemo(() => {
    const items: BrandEntity[] = [];
    const seenDomains = new Set<string>();

    if (workspaceBrandDomain) {
      seenDomains.add(workspaceBrandDomain);
      items.push({
        name: workspaceBrandName,
        domain: workspaceBrandDomain,
        faviconUrl: `https://www.google.com/s2/favicons?domain=${workspaceBrandDomain}&sz=64`,
        isWorkspaceBrand: true,
      });
    }

    competitors.forEach((competitor) => {
      const domain = normalizeDomain(competitor.domain);
      if (!domain || seenDomains.has(domain)) return;
      seenDomains.add(domain);
      items.push({
        name: competitor.name || extractBrandName(domain),
        domain,
        faviconUrl:
          competitor.favicon_url ||
          `https://www.google.com/s2/favicons?domain=${domain}&sz=64`,
        isWorkspaceBrand: false,
      });
    });

    return items;
  }, [competitors, workspaceBrandDomain, workspaceBrandName]);

  useEffect(() => {
    if (selectedBrandDomain && brandEntities.some((brand) => brand.domain === selectedBrandDomain)) {
      return;
    }

    if (workspaceBrandDomain) {
      setSelectedBrandDomain(workspaceBrandDomain);
      return;
    }

    if (brandEntities.length > 0) {
      setSelectedBrandDomain(brandEntities[0].domain);
    }
  }, [brandEntities, selectedBrandDomain, workspaceBrandDomain]);

  const promptById = useMemo(() => {
    const map = new Map<string, string>();
    prompts.forEach((prompt) => {
      map.set(prompt.id, prompt.text);
    });
    return map;
  }, [prompts]);

  const latestCompletedRunId = promptRuns[0]?.id || null;
  const previousCompletedRunId = promptRuns[1]?.id || null;

  const responsesInRange = useMemo(
    () =>
      appliedPreset === 'today' && latestCompletedRunId
        ? responses.filter((response) => response.run_id === latestCompletedRunId)
        : responses.filter((response) => isWithinDateRange(response.created_at, rangeStart, rangeEnd)),
    [appliedPreset, latestCompletedRunId, responses, rangeStart, rangeEnd]
  );

  const mentionsInRange = useMemo(
    () =>
      appliedPreset === 'today' && latestCompletedRunId
        ? mentions.filter((mention) => mention.run_id === latestCompletedRunId)
        : mentions.filter((mention) => isWithinDateRange(mention.created_at, rangeStart, rangeEnd)),
    [appliedPreset, latestCompletedRunId, mentions, rangeStart, rangeEnd]
  );

  const citationsInRange = useMemo(
    () =>
      appliedPreset === 'today' && latestCompletedRunId
        ? citations.filter((citation) => citation.run_id === latestCompletedRunId)
        : citations.filter((citation) => isWithinDateRange(citation.created_at, rangeStart, rangeEnd)),
    [appliedPreset, citations, latestCompletedRunId, rangeStart, rangeEnd]
  );

  const effectiveRangeData = useMemo(
    () => buildEffectiveRangeData(responsesInRange, mentionsInRange, citationsInRange),
    [citationsInRange, mentionsInRange, responsesInRange]
  );

  const effectiveResponsesInRange = effectiveRangeData.responses;
  const effectiveMentionsInRange = effectiveRangeData.mentions;
  const effectiveCitationsInRange = effectiveRangeData.citations;

  const previousRange = useMemo(
    () => computePreviousRange(rangeStart, rangeEnd),
    [rangeStart, rangeEnd]
  );

  const responsesInPreviousRange = useMemo(() => {
    if (appliedPreset === 'today' && previousCompletedRunId) {
      return responses.filter((response) => response.run_id === previousCompletedRunId);
    }
    if (!previousRange) return [];
    return responses.filter((response) =>
      isWithinDateRange(response.created_at, previousRange.start, previousRange.end)
    );
  }, [appliedPreset, previousCompletedRunId, previousRange, responses]);

  const mentionsInPreviousRange = useMemo(() => {
    if (appliedPreset === 'today' && previousCompletedRunId) {
      return mentions.filter((mention) => mention.run_id === previousCompletedRunId);
    }
    if (!previousRange) return [];
    return mentions.filter((mention) =>
      isWithinDateRange(mention.created_at, previousRange.start, previousRange.end)
    );
  }, [appliedPreset, mentions, previousCompletedRunId, previousRange]);

  const citationsInPreviousRange = useMemo(() => {
    if (appliedPreset === 'today' && previousCompletedRunId) {
      return citations.filter((citation) => citation.run_id === previousCompletedRunId);
    }
    if (!previousRange) return [];
    return citations.filter((citation) =>
      isWithinDateRange(citation.created_at, previousRange.start, previousRange.end)
    );
  }, [appliedPreset, citations, previousCompletedRunId, previousRange]);

  const effectivePreviousRangeData = useMemo(
    () => buildEffectiveRangeData(
      responsesInPreviousRange,
      mentionsInPreviousRange,
      citationsInPreviousRange
    ),
    [citationsInPreviousRange, mentionsInPreviousRange, responsesInPreviousRange]
  );

  const effectiveResponsesInPreviousRange = effectivePreviousRangeData.responses;
  const effectiveMentionsInPreviousRange = effectivePreviousRangeData.mentions;

  const legacyResponseMentionsBrand = useCallback(
    (response: PromptResponseRow, brandDomain: string) => {
      if (!brandDomain) return false;
      if (brandDomain === workspaceBrandDomain) return response.brand_mentioned;

      const rawMentions = response.competitors_mentioned;
      if (!Array.isArray(rawMentions)) return false;

      return rawMentions.some((item) => {
        if (!item || typeof item !== 'object') return false;
        const mention = item as LegacyCompetitorMentionEntry;
        const domain = typeof mention.domain === 'string' ? normalizeDomain(mention.domain) : '';
        return mention.mentioned === true && domainMatches(domain, brandDomain);
      });
    },
    [workspaceBrandDomain]
  );

  const buildResponseIdSetForBrand = useCallback(
    (
      brandDomain: string,
      mentionRows: MentionRow[],
      citationRows: CitationRow[],
      responseRows: PromptResponseRow[]
    ) => {
      const ids = new Set<string>();

      mentionRows.forEach((mention) => {
        if (domainMatches(mention.brand_domain, brandDomain)) {
          ids.add(mention.prompt_response_id);
        }
      });

      citationRows.forEach((citation) => {
        if (citationMatchesBrand(citation, brandDomain)) {
          ids.add(citation.prompt_response_id);
        }
      });

      if (ids.size === 0 && brandDomain === workspaceBrandDomain) {
        responseRows.forEach((response) => {
          if (response.brand_mentioned) {
            ids.add(response.id);
          }
        });
      }

      if (ids.size === 0) {
        responseRows.forEach((response) => {
          if (legacyResponseMentionsBrand(response, brandDomain)) {
            ids.add(response.id);
          }
        });
      }

      return ids;
    },
    [legacyResponseMentionsBrand, workspaceBrandDomain]
  );

  const buildSentimentScoresForBrand = useCallback(
    (brandDomain: string, mentionRows: MentionRow[], responseRows: PromptResponseRow[]) => {
      const scores = mentionRows
        .filter((mention) => domainMatches(mention.brand_domain, brandDomain))
        .map((mention) => sentimentToScore(mention.sentiment))
        .filter((score): score is number => score !== null);

      if (scores.length === 0 && brandDomain === workspaceBrandDomain) {
        return responseRows
          .map((response) => sentimentToScore(response.brand_sentiment))
          .filter((score): score is number => score !== null);
      }

      return scores;
    },
    [workspaceBrandDomain]
  );

  const buildPositionValuesForBrand = useCallback(
    (brandDomain: string, mentionRows: MentionRow[], responseRows: PromptResponseRow[]) => {
      const positions = mentionRows
        .filter((mention) => domainMatches(mention.brand_domain, brandDomain) && mention.position !== null)
        .map((mention) => mention.position as number);

      if (positions.length === 0 && brandDomain === workspaceBrandDomain) {
        return responseRows
          .filter((response) => response.brand_position !== null)
          .map((response) => response.brand_position as number);
      }

      return positions;
    },
    [workspaceBrandDomain]
  );

  const getBrandCitationRows = useCallback(
    (brandDomain: string, list: CitationRow[]) =>
      list.filter((citation) => citationMatchesBrand(citation, brandDomain)),
    []
  );

  const buildBrandMentionBreakdown = useCallback(
    (brandDomain: string, mentionRows: MentionRow[], citationRows: CitationRow[], responseRows: PromptResponseRow[]) => {
      const totals = { total: 0, direct: 0, indirect: 0 };
      const brandMentions = mentionRows.filter((mention) => domainMatches(mention.brand_domain, brandDomain));
      const responseById = new Map(responseRows.map((response) => [response.id, response] as const));
      const citationResponseIds = new Set<string>();

      if (citationRows.length > 0) {
        citationRows.forEach((citation) => {
          citationResponseIds.add(citation.prompt_response_id);
          totals.total += 1;

          if (citationIsDirectForBrand(citation, brandDomain)) {
            totals.direct += 1;
          } else {
            totals.indirect += 1;
          }
        });
      }

      const applyCount = (count: number, responseId: string) => {
        const brandCitations = citationRows.filter((citation) => citation.prompt_response_id === responseId);
        if (brandCitations.some((citation) => citationIsDirectForBrand(citation, brandDomain))) {
          totals.direct += count;
          return;
        }

        if (brandCitations.length > 0) {
          totals.indirect += count;
          return;
        }

        const response = responseById.get(responseId);
        const legacyCitationDomains = toStringArray(response?.citations).map(normalizeDomain);

        if (legacyCitationDomains.some((domain) => domainMatches(domain, brandDomain))) {
          totals.direct += count;
        } else {
          totals.indirect += count;
        }
      };

      if (brandMentions.length > 0) {
        brandMentions.forEach((mention) => {
          if (citationResponseIds.has(mention.prompt_response_id)) return;

          const count = Math.max(mention.mention_count || 0, 1);
          totals.total += count;
          applyCount(count, mention.prompt_response_id);
        });
        return totals;
      }

      if (brandDomain === workspaceBrandDomain) {
        responseRows
          .filter((response) => response.brand_mentioned)
          .forEach((response) => {
            if (citationResponseIds.has(response.id)) return;

            totals.total += 1;
            applyCount(1, response.id);
          });

        return totals;
      }

      responseRows
        .filter((response) => legacyResponseMentionsBrand(response, brandDomain))
        .forEach((response) => {
          if (citationResponseIds.has(response.id)) return;

          totals.total += 1;
          applyCount(1, response.id);
        });

      return totals;
    },
    [legacyResponseMentionsBrand, workspaceBrandDomain]
  );

  const workspaceResponseIds = useMemo(
    () => buildResponseIdSetForBrand(
      workspaceBrandDomain,
      effectiveMentionsInRange,
      effectiveCitationsInRange,
      effectiveResponsesInRange
    ),
    [
      buildResponseIdSetForBrand,
      workspaceBrandDomain,
      effectiveMentionsInRange,
      effectiveCitationsInRange,
      effectiveResponsesInRange,
    ]
  );

  const workspaceVisibility = effectiveResponsesInRange.length > 0
    ? (workspaceResponseIds.size / effectiveResponsesInRange.length) * 100
    : null;

  const workspaceSentimentScores = useMemo(
    () => buildSentimentScoresForBrand(workspaceBrandDomain, effectiveMentionsInRange, effectiveResponsesInRange),
    [buildSentimentScoresForBrand, workspaceBrandDomain, effectiveMentionsInRange, effectiveResponsesInRange]
  );

  const workspaceSentimentAverage = average(workspaceSentimentScores);
  const formattedWorkspaceSentimentAverage =
    workspaceSentimentAverage !== null && Number.isFinite(workspaceSentimentAverage)
      ? `${Math.round(workspaceSentimentAverage)}%`
      : '—';
  const workspacePositionValues = useMemo(
    () => buildPositionValuesForBrand(workspaceBrandDomain, effectiveMentionsInRange, effectiveResponsesInRange),
    [buildPositionValuesForBrand, workspaceBrandDomain, effectiveMentionsInRange, effectiveResponsesInRange]
  );
  const workspaceAveragePosition = average(workspacePositionValues);

  const workspaceCitationCounts = useMemo(() => {
    if (!workspaceBrandDomain) return { total: 0, direct: 0, indirect: 0 };
    return buildBrandMentionBreakdown(
      workspaceBrandDomain,
      effectiveMentionsInRange,
      getBrandCitationRows(workspaceBrandDomain, effectiveCitationsInRange),
      effectiveResponsesInRange
    );
  }, [
    buildBrandMentionBreakdown,
    getBrandCitationRows,
    effectiveCitationsInRange,
    effectiveMentionsInRange,
    effectiveResponsesInRange,
    workspaceBrandDomain,
  ]);
  const workspaceTotalBrandMentions = workspaceCitationCounts.total;

  const previousWorkspaceResponseIds = useMemo(
    () =>
      buildResponseIdSetForBrand(
        workspaceBrandDomain,
        effectiveMentionsInPreviousRange,
        effectivePreviousRangeData.citations,
        effectiveResponsesInPreviousRange
      ),
    [
      buildResponseIdSetForBrand,
      effectiveMentionsInPreviousRange,
      effectivePreviousRangeData.citations,
      effectiveResponsesInPreviousRange,
      workspaceBrandDomain,
    ]
  );

  const previousWorkspaceVisibility = previousRange && effectiveResponsesInPreviousRange.length > 0
    ? (previousWorkspaceResponseIds.size / effectiveResponsesInPreviousRange.length) * 100
    : null;

  const previousWorkspaceSentimentScores = useMemo(
    () =>
      buildSentimentScoresForBrand(
        workspaceBrandDomain,
        effectiveMentionsInPreviousRange,
        effectiveResponsesInPreviousRange
      ),
    [
      buildSentimentScoresForBrand,
      effectiveMentionsInPreviousRange,
      effectiveResponsesInPreviousRange,
      workspaceBrandDomain,
    ]
  );
  const previousWorkspaceSentimentAverage = average(previousWorkspaceSentimentScores);

  const previousWorkspacePositionValues = useMemo(
    () =>
      buildPositionValuesForBrand(
        workspaceBrandDomain,
        effectiveMentionsInPreviousRange,
        effectiveResponsesInPreviousRange
      ),
    [
      buildPositionValuesForBrand,
      effectiveMentionsInPreviousRange,
      effectiveResponsesInPreviousRange,
      workspaceBrandDomain,
    ]
  );
  const previousWorkspaceAveragePosition = average(previousWorkspacePositionValues);

  const previousWorkspaceCitationCounts = useMemo(() => {
    if (!workspaceBrandDomain) return { total: 0, direct: 0, indirect: 0 };
    return buildBrandMentionBreakdown(
      workspaceBrandDomain,
      effectiveMentionsInPreviousRange,
      getBrandCitationRows(workspaceBrandDomain, effectivePreviousRangeData.citations),
      effectiveResponsesInPreviousRange
    );
  }, [
    buildBrandMentionBreakdown,
    getBrandCitationRows,
    effectiveMentionsInPreviousRange,
    effectivePreviousRangeData.citations,
    effectiveResponsesInPreviousRange,
    workspaceBrandDomain,
  ]);

  const selectedPeriodDayCount = useMemo(
    () => getSelectedPeriodDayCount(rangeStart, rangeEnd),
    [rangeEnd, rangeStart]
  );

  const currentPeriodDescription = useMemo(
    () => getCurrentPeriodDescription(appliedPreset, selectedPeriodDayCount),
    [appliedPreset, selectedPeriodDayCount]
  );

  const previousPeriodDescription = useMemo(
    () => (previousRange ? getPreviousPeriodDescription(appliedPreset, selectedPeriodDayCount) : null),
    [appliedPreset, previousRange, selectedPeriodDayCount]
  );

  const visibilityTrend = useMemo(
    () =>
      buildMetricTrend({
        label: 'Visibility',
        current: workspaceVisibility,
        previous: previousWorkspaceVisibility,
        currentPeriodDescription,
        previousPeriodDescription,
        formatValue: (value) => `${Math.round(value)}%`,
      }),
    [currentPeriodDescription, previousPeriodDescription, previousWorkspaceVisibility, workspaceVisibility]
  );

  const sentimentTrend = useMemo(
    () =>
      buildMetricTrend({
        label: 'Average sentiment',
        current: workspaceSentimentAverage,
        previous: previousWorkspaceSentimentAverage,
        currentPeriodDescription,
        previousPeriodDescription,
        formatValue: (value) => `${Math.round(value)}%`,
      }),
    [
      currentPeriodDescription,
      previousPeriodDescription,
      previousWorkspaceSentimentAverage,
      workspaceSentimentAverage,
    ]
  );

  const positionTrend = useMemo(
    () =>
      buildMetricTrend({
        label: 'Average position',
        current: workspaceAveragePosition,
        previous: previousWorkspaceAveragePosition,
        higherIsBetter: false,
        currentPeriodDescription,
        previousPeriodDescription,
      }),
    [
      currentPeriodDescription,
      previousPeriodDescription,
      previousWorkspaceAveragePosition,
      workspaceAveragePosition,
    ]
  );

  const mentionsTrend = useMemo(
    () =>
      buildMetricTrend({
        label: 'Total brand mentions',
        current: workspaceTotalBrandMentions,
        previous: previousWorkspaceCitationCounts.total,
        currentPeriodDescription,
        previousPeriodDescription,
      }),
    [
      currentPeriodDescription,
      previousPeriodDescription,
      previousWorkspaceCitationCounts.total,
      workspaceTotalBrandMentions,
    ]
  );

  const selectedBrand = useMemo(
    () => brandEntities.find((brand) => brand.domain === selectedBrandDomain) || null,
    [brandEntities, selectedBrandDomain]
  );

  const brandByDomain = useMemo(
    () => new Map(brandEntities.map((brand) => [brand.domain, brand] as const)),
    [brandEntities]
  );

  const mentionsByResponseId = useMemo(() => {
    const map = new Map<string, MentionRow[]>();
    mentionsInRange.forEach((row) => {
      const existing = map.get(row.prompt_response_id) || [];
      existing.push(row);
      map.set(row.prompt_response_id, existing);
    });
    return map;
  }, [mentionsInRange]);

  const citationsByResponseId = useMemo(() => {
    const map = new Map<string, CitationRow[]>();
    citationsInRange.forEach((row) => {
      const existing = map.get(row.prompt_response_id) || [];
      existing.push(row);
      map.set(row.prompt_response_id, existing);
    });
    return map;
  }, [citationsInRange]);

  const buildBrandsForResponse = useCallback((response: PromptResponseRow) => {
    const items: SearchResponseModalItem['brands'] = [];
    const seen = new Set<string>();

    if (response.brand_mentioned && workspaceBrandDomain && brandByDomain.has(workspaceBrandDomain)) {
      items.push(brandByDomain.get(workspaceBrandDomain)!);
      seen.add(workspaceBrandDomain);
    }

    if (Array.isArray(response.competitors_mentioned)) {
      for (const raw of response.competitors_mentioned as Array<Record<string, unknown>>) {
        const mentioned = raw.mentioned === true;
        const domain = typeof raw.domain === 'string' ? normalizeDomain(raw.domain) : '';
        if (!mentioned || !domain || seen.has(domain)) continue;

        if (brandByDomain.has(domain)) {
          items.push(brandByDomain.get(domain)!);
        } else {
          items.push({
            domain,
            name: extractBrandName(domain),
            faviconUrl: `https://www.google.com/s2/favicons?domain=${domain}&sz=64`,
            isWorkspaceBrand: false,
          });
        }
        seen.add(domain);
      }
    }

    return items;
  }, [brandByDomain, workspaceBrandDomain]);

  const getMentionStatusForResponse = useCallback((response: PromptResponseRow, brandDomain: string): SearchModalMentionStatus => {
    if (!brandDomain) return 'none';

    const normalizedBrandDomain = normalizeDomain(brandDomain);
    const responseMentions = mentionsByResponseId.get(response.id) || [];
    const responseCitations = citationsByResponseId.get(response.id) || [];

    const hasStructuredMention = responseMentions.some((row) => domainMatches(row.brand_domain || '', normalizedBrandDomain));
    const hasDirectCitation = responseCitations.some((row) => {
      const associatedDomains = toStringArray(row.associated_brand_domains).map(normalizeDomain);
      return associatedDomains.some((domain) => domainMatches(domain, normalizedBrandDomain))
        && domainMatches(row.citation_domain || '', normalizedBrandDomain);
    });
    const hasIndirectCitation = responseCitations.some((row) => {
      const associatedDomains = toStringArray(row.associated_brand_domains).map(normalizeDomain);
      return associatedDomains.some((domain) => domainMatches(domain, normalizedBrandDomain))
        && !domainMatches(row.citation_domain || '', normalizedBrandDomain);
    });

    if (hasDirectCitation) return 'mentioned';
    if (hasIndirectCitation && hasStructuredMention) return 'indirect';

    if (normalizedBrandDomain === workspaceBrandDomain && response.brand_mentioned) {
      return hasIndirectCitation ? 'indirect' : 'mentioned';
    }

    if (Array.isArray(response.competitors_mentioned)) {
      const legacyMention = (response.competitors_mentioned as Array<Record<string, unknown>>).some((raw) => {
        const mentioned = raw.mentioned === true;
        const domain = typeof raw.domain === 'string' ? normalizeDomain(raw.domain) : '';
        return mentioned && domainMatches(domain, normalizedBrandDomain);
      });

      if (legacyMention) return hasIndirectCitation ? 'indirect' : 'mentioned';
    }

    if (hasStructuredMention) return hasIndirectCitation ? 'indirect' : 'mentioned';
    return 'none';
  }, [citationsByResponseId, mentionsByResponseId, workspaceBrandDomain]);

  const selectedBrandCounts = useMemo(() => {
    if (!selectedBrandDomain) return { total: 0, direct: 0, indirect: 0 };
    return buildBrandMentionBreakdown(
      selectedBrandDomain,
      effectiveMentionsInRange,
      getBrandCitationRows(selectedBrandDomain, effectiveCitationsInRange),
      effectiveResponsesInRange
    );
  }, [
    buildBrandMentionBreakdown,
    effectiveCitationsInRange,
    effectiveMentionsInRange,
    effectiveResponsesInRange,
    getBrandCitationRows,
    selectedBrandDomain,
  ]);

  const brandVisibilityBars = useMemo(
    () =>
      brandEntities.map((brand) => {
        const breakdown = buildBrandMentionBreakdown(
          brand.domain,
          effectiveMentionsInRange,
          getBrandCitationRows(brand.domain, effectiveCitationsInRange),
          effectiveResponsesInRange
        );

        return {
          domain: brand.domain,
          name: brand.name,
          total: breakdown.total,
          direct: breakdown.direct,
          indirect: breakdown.indirect,
        };
      }),
    [
      brandEntities,
      buildBrandMentionBreakdown,
      effectiveCitationsInRange,
      effectiveMentionsInRange,
      effectiveResponsesInRange,
      getBrandCitationRows,
    ]
  );

  const maxBrandVisibilityTotal = useMemo(
    () => Math.max(...brandVisibilityBars.map((bar) => bar.total), 0),
    [brandVisibilityBars]
  );

  const latestCompletedTimelineDate = useMemo(() => {
    const workspaceRunTimestamp = workspace?.last_prompt_run_at
      ? new Date(workspace.last_prompt_run_at).getTime()
      : Number.NaN;

    if (!Number.isNaN(workspaceRunTimestamp)) {
      return startOfDay(new Date(workspaceRunTimestamp));
    }

    const latestResponseTimestamp = responses
      .map((item) => new Date(item.created_at).getTime())
      .filter((value) => !Number.isNaN(value))
      .reduce<number | null>((latest, value) => (latest === null || value > latest ? value : latest), null);

    return latestResponseTimestamp === null ? startOfDay(new Date()) : startOfDay(new Date(latestResponseTimestamp));
  }, [responses, workspace?.last_prompt_run_at]);

  const timelineDateRange = useMemo(() => {
    if (appliedPreset === 'today') {
      const firstResponseTimestamp = responses
        .map((item) => new Date(item.created_at).getTime())
        .filter((value) => !Number.isNaN(value))
        .reduce<number | null>((min, value) => (min === null || value < min ? value : min), null);

      const timelineEnd = latestCompletedTimelineDate;
      const defaultStart = new Date(
        timelineEnd.getFullYear(),
        timelineEnd.getMonth(),
        timelineEnd.getDate() - 6
      );
      const earliestStart = firstResponseTimestamp === null
        ? timelineEnd
        : startOfDay(new Date(firstResponseTimestamp));

      return {
        start: earliestStart > defaultStart ? earliestStart : defaultStart,
        end: endOfDay(timelineEnd),
      };
    }

    if (rangeStart || rangeEnd) {
      return {
        start: startOfDay(rangeStart ?? rangeEnd ?? new Date()),
        end: endOfDay(rangeEnd ?? rangeStart ?? new Date()),
      };
    }

    const allTimestamps = responsesInRange
      .map((item) => new Date(item.created_at).getTime())
      .filter((value) => !Number.isNaN(value));

    if (allTimestamps.length === 0) return null;

    return {
      start: startOfDay(new Date(Math.min(...allTimestamps))),
      end: endOfDay(new Date(Math.max(...allTimestamps))),
    };
  }, [appliedPreset, latestCompletedTimelineDate, rangeEnd, rangeStart, responses, responsesInRange]);

  const brandTimelineSeries = useMemo(() => {
    const bounds = timelineDateRange;
    if (!bounds) return [];

    const days: { key: string; label: string; start: Date; end: Date }[] = [];
    for (
      let cursor = new Date(bounds.start);
      cursor <= bounds.end;
      cursor = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() + 1)
    ) {
      const dayStart = startOfDay(cursor);
      days.push({
        key: dayStart.toISOString(),
        label: formatTimelineLabel(dayStart),
        start: dayStart,
        end: endOfDay(dayStart),
      });
    }

    return brandEntities.map((brand, index) => ({
      domain: brand.domain,
      name: brand.name,
      color: BRAND_COLORS[index % BRAND_COLORS.length],
      points: days.map((day) => {
        const dayResponses = responsesInRange.filter((response) =>
          isWithinDateRange(response.created_at, day.start, day.end)
        );
        const latestResponsesByPromptModel = new Map<string, PromptResponseRow>();

        dayResponses.forEach((response) => {
          const key = getPromptModelKey(response);
          const existing = latestResponsesByPromptModel.get(key);

          if (!existing || new Date(response.created_at).getTime() > new Date(existing.created_at).getTime()) {
            latestResponsesByPromptModel.set(key, response);
          }
        });

        const effectiveDayResponses = Array.from(latestResponsesByPromptModel.values());
        const effectiveResponseIds = new Set(effectiveDayResponses.map((response) => response.id));
        const dayMentions = mentionsInRange.filter(
          (mention) =>
            effectiveResponseIds.has(mention.prompt_response_id) &&
            isWithinDateRange(mention.created_at, day.start, day.end)
        );
        const dayCitations = citationsInRange.filter(
          (citation) =>
            effectiveResponseIds.has(citation.prompt_response_id) &&
            isWithinDateRange(citation.created_at, day.start, day.end)
        );
        const dayResponseIds = buildResponseIdSetForBrand(
          brand.domain,
          dayMentions,
          dayCitations,
          effectiveDayResponses
        );
        const breakdown = buildBrandMentionBreakdown(
          brand.domain,
          dayMentions,
          getBrandCitationRows(brand.domain, dayCitations),
          effectiveDayResponses
        );

        return {
          key: day.key,
          label: day.label,
          total: breakdown.total,
          visibility:
            effectiveDayResponses.length > 0
              ? (dayResponseIds.size / effectiveDayResponses.length) * 100
              : null,
        };
      }),
    }));
  }, [
    brandEntities,
    buildBrandMentionBreakdown,
    buildResponseIdSetForBrand,
    citationsInRange,
    getBrandCitationRows,
    mentionsInRange,
    responsesInRange,
    timelineDateRange,
  ]);

  const visibleTimelineSeries = useMemo(
    () => brandTimelineSeries.filter((series) => !hiddenTimelineDomains.includes(series.domain)),
    [brandTimelineSeries, hiddenTimelineDomains]
  );

  const maxTimelineMentions = useMemo(
    () => Math.max(...brandTimelineSeries.flatMap((series) => series.points.map((point) => point.total)), 0),
    [brandTimelineSeries]
  );

  const timelineTickIndices = useMemo(
    () => getTimelineTickIndices((visibleTimelineSeries[0] ?? brandTimelineSeries[0])?.points.length ?? 0),
    [brandTimelineSeries, visibleTimelineSeries]
  );

  const buildMentionSourcesForBrand = useCallback((brandDomain: string) => {
    const sourceMap = new Map<string, SourceListRow>();
    const brandCitationRows = getBrandCitationRows(brandDomain, effectiveCitationsInRange);
    const brandMentionRows = effectiveMentionsInRange.filter((mention) =>
      domainMatches(mention.brand_domain, brandDomain)
    );

    brandCitationRows.forEach((citation) => {
      const domain = normalizeDomain(citation.citation_domain || citation.citation_url || '');
      if (!domain) return;

      const key = `site:${domain}`;
      const existing = sourceMap.get(key);
      if (existing) {
        existing.count += 1;
        return;
      }

      sourceMap.set(key, {
        key,
        label: extractBrandName(domain),
        sublabel: domain,
        count: 1,
        kind: 'website',
        faviconUrl: `https://www.google.com/s2/favicons?domain=${domain}&sz=64`,
      });
    });

    const modelRows =
      brandMentionRows.length > 0
        ? brandMentionRows
        : brandDomain === workspaceBrandDomain
          ? effectiveResponsesInRange
              .filter((response) => response.brand_mentioned)
              .map((response) => ({
                prompt_response_id: response.id,
                prompt_id: response.prompt_id,
                model: response.model,
                brand_name: workspaceBrandName,
                brand_domain: workspaceBrandDomain,
                mentioned: true,
                position: response.brand_position,
                mention_count: 1,
                sentiment: response.brand_sentiment,
                created_at: response.created_at,
              }))
          : [];
    
    modelRows.forEach((row) => {
      const meta = getLlmMeta(row.model);
      const key = `llm:${meta.name}`;
      const existing = sourceMap.get(key);
      if (existing) {
        existing.count += 1;
        return;
      }

      sourceMap.set(key, {
        key,
        label: meta.name,
        sublabel: 'LLM',
        count: 1,
        kind: 'llm',
        color: meta.color,
        icon: meta.icon,
        shortLabel: meta.short,
      });
    });

    return Array.from(sourceMap.values())
      .sort((a, b) => b.count - a.count)
      .slice(0, 6);
  }, [
    effectiveCitationsInRange,
    domainMatches,
    effectiveMentionsInRange,
    effectiveResponsesInRange,
    getBrandCitationRows,
    workspaceBrandDomain,
    workspaceBrandName,
  ]);

  const rankingRows = useMemo(() => {
    const rows = brandEntities.map((brand, index) => {
        const currentResponseIds = buildResponseIdSetForBrand(
          brand.domain,
          effectiveMentionsInRange,
          effectiveCitationsInRange,
          effectiveResponsesInRange
        );
        const previousResponseIds = buildResponseIdSetForBrand(
          brand.domain,
          effectiveMentionsInPreviousRange,
          effectivePreviousRangeData.citations,
          effectiveResponsesInPreviousRange
        );

        const currentVisibility = effectiveResponsesInRange.length > 0
          ? (currentResponseIds.size / effectiveResponsesInRange.length) * 100
          : null;

        const previousVisibility = previousRange && effectiveResponsesInPreviousRange.length > 0
          ? (previousResponseIds.size / effectiveResponsesInPreviousRange.length) * 100
          : null;
        const breakdown = buildBrandMentionBreakdown(
          brand.domain,
          effectiveMentionsInRange,
          getBrandCitationRows(brand.domain, effectiveCitationsInRange),
          effectiveResponsesInRange
        );

        const sentimentScores = buildSentimentScoresForBrand(
          brand.domain,
          effectiveMentionsInRange,
          effectiveResponsesInRange
        );
        const positions = buildPositionValuesForBrand(
          brand.domain,
          effectiveMentionsInRange,
          effectiveResponsesInRange
        );

        return {
          row: {
            domain: brand.domain,
            name: brand.name,
            faviconUrl: brand.faviconUrl,
            isWorkspaceBrand: brand.isWorkspaceBrand,
            totalMentions: breakdown.total,
            visibility: currentVisibility,
            visibleResponses: currentResponseIds.size,
            totalResponses: effectiveResponsesInRange.length,
            sentiment: average(sentimentScores),
            avgPosition: average(positions),
            mentionSources: buildMentionSourcesForBrand(brand.domain).slice(0, 3),
            trendDelta:
              currentVisibility !== null && previousVisibility !== null
                ? currentVisibility - previousVisibility
                : null,
          },
          sortOrder: brand.isWorkspaceBrand ? -100 : index,
        };
      })
      .sort((a, b) => {
        if (b.row.visibility !== a.row.visibility) {
          return (b.row.visibility ?? -1) - (a.row.visibility ?? -1);
        }
        return a.sortOrder - b.sortOrder;
      });

    return rows.map((entry) => entry.row);
  }, [
    brandEntities,
    buildBrandMentionBreakdown,
    buildMentionSourcesForBrand,
    buildPositionValuesForBrand,
    buildResponseIdSetForBrand,
    buildSentimentScoresForBrand,
    effectiveCitationsInRange,
    effectiveMentionsInPreviousRange,
    effectiveMentionsInRange,
    effectiveResponsesInPreviousRange,
    effectiveResponsesInRange,
    getBrandCitationRows,
    previousRange,
  ]);

  const recentPromptSearches = useMemo(
    () =>
      responsesInRange.slice(0, 12).map((response) => {
        const structuredCitations = citationsByResponseId.get(response.id) || [];
        const storedCitationValues = Array.isArray(response.citations)
          ? response.citations.filter((item): item is string => typeof item === 'string')
          : [];
        const citationMap = new Map<string, { url: string; domain: string }>();

        structuredCitations.forEach((citation) => {
          const url = typeof citation.citation_url === 'string' ? citation.citation_url : '';
          const domain = typeof citation.citation_domain === 'string' ? citation.citation_domain : normalizeDomain(url);
          if (!url || citationMap.has(url)) return;
          citationMap.set(url, { url, domain });
        });

        storedCitationValues.forEach((url) => {
          if (!url || citationMap.has(url)) return;
          citationMap.set(url, { url, domain: normalizeDomain(url) });
        });

        const citations = Array.from(citationMap.values());
        const responseLinks = mergeResponseLinks(citations, parseProviderLinks(response.provider_links));

        return {
          id: response.id,
          promptId: response.prompt_id,
          promptText: (response.prompt_id ? promptById.get(response.prompt_id) : null) || 'Prompt deleted',
          createdAt: response.created_at,
          model: response.model,
          responseText: response.response_text,
          brands: buildBrandsForResponse(response),
          citations,
          responseLinks,
          mentionStatus: getMentionStatusForResponse(response, selectedBrandDomain || workspaceBrandDomain),
        };
      }),
    [buildBrandsForResponse, citationsByResponseId, getMentionStatusForResponse, promptById, responsesInRange, selectedBrandDomain, workspaceBrandDomain]
  );

  const selectedRecentSearch = useMemo(
    () => recentPromptSearches.find((item) => item.id === selectedRecentSearchId) || null,
    [recentPromptSearches, selectedRecentSearchId]
  );

  const highlightedTimelineDomain = activeTimelineDomain;

  const buildMentionsHref = useCallback(
    (options?: { brandDomain?: string; promptId?: string | null }) => {
      const params = new URLSearchParams();
      const targetBrand = options?.brandDomain;

      if (targetBrand) {
        params.set('brand', targetBrand);
      }

      if (options?.promptId) {
        params.set('prompt', options.promptId);
      }

      const query = params.toString();
      return query ? `/mentions?${query}` : '/mentions';
    },
    []
  );

  const renderTrendIcon = (status: MetricTrend['status']) => {
    if (status === 'positive') {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          <path d="m18 15-6-6-6 6" />
        </svg>
      );
    }

    if (status === 'negative') {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          <path d="m6 9 6 6 6-6" />
        </svg>
      );
    }

    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 12h14" />
      </svg>
    );
  };

  const renderMetricTrend = (trend: MetricTrend, preferredAlign: 'center' | 'left' | 'right' = 'center') => (
    <span className={`info-tooltip-wrapper stat-card-trend is-${trend.status} is-align-${preferredAlign}`}>
      <span className={`stat-card-trend-value is-${trend.status}`}>
        <span className="stat-card-change-icon">{renderTrendIcon(trend.status)}</span>
        <span>{trend.deltaPercent === null ? '0%' : `${trend.deltaPercent}%`}</span>
      </span>
      <span className="info-tooltip">{trend.tooltip}</span>
    </span>
  );

  return (
    <div>
      <div className="spacer-xsmall"></div>

      <div className="overview-header">
        <div className="overview-header-left">
          <h1>Overview</h1>
        </div>

        <div className="date-range-wrapper" ref={datePickerRef}>
          <div
            className="date-range-toggle"
            onClick={() => (datePickerVisible ? dismissDatePicker() : openDatePicker())}
          >
            <div className="date-range-selected">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M8 2v4" />
                <path d="M16 2v4" />
                <rect width="18" height="18" x="3" y="4" rx="2" />
                <path d="M3 10h18" />
              </svg>
              <div className="text-size-small text-weight-medium text-colour-dark-brown">{dateLabel}</div>
            </div>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={isDatePickerOpen ? { transform: 'rotate(180deg)', transition: 'transform 0.2s' } : { transition: 'transform 0.2s' }}>
              <path d="m6 9 6 6 6-6" />
            </svg>
          </div>

          {datePickerVisible && (
            <div className={`date-range-dropdown ${isDatePickerOpen ? 'is-visible' : ''}`}>
              <div className="date-range-presets">
                {PRESETS.map((preset) => (
                  <button
                    key={preset.key}
                    className={`date-range-preset ${activePreset === preset.key ? 'is-active' : ''}`}
                    onClick={() => handlePresetClick(preset.key)}
                    type="button"
                  >
                    {preset.label}
                    {activePreset === preset.key && (
                      <span className="date-range-preset-check">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 6 9 17l-5-5" />
                        </svg>
                      </span>
                    )}
                  </button>
                ))}
              </div>
              <div style={{ flex: 1 }}>
                <div className="date-range-calendars">
                  <Calendar
                    year={leftYear}
                    month={leftMonth}
                    rangeStart={tempStart}
                    rangeEnd={tempEnd}
                    onDayClick={handleDayClick}
                    onPrevMonth={handlePrevMonth}
                    onNextMonth={handleNextMonth}
                    showPrevNav={true}
                    showNextNav={false}
                  />
                  <Calendar
                    year={rightYear}
                    month={rightMonth}
                    rangeStart={tempStart}
                    rangeEnd={tempEnd}
                    onDayClick={handleDayClick}
                    onPrevMonth={handlePrevMonth}
                    onNextMonth={handleNextMonth}
                    showPrevNav={false}
                    showNextNav={true}
                  />
                </div>
                <div className="date-range-footer">
                  <button className="button is-clear" onClick={handleCancel} type="button">
                    Cancel
                  </button>
                  <button className="button is-apply" onClick={handleApply} type="button">
                    Apply
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="stat-cards-row">
        <div className="stat-card">
          <div className="stat-card-header">
            <span className="stat-card-label">Visibility</span>
            <div className="stat-card-header-side">
              <InfoTooltip text="How often your brand appears across tracked prompt searches in the selected date range." />
            </div>
          </div>
          <div className="stat-card-value-with-range">
            <div className="stat-card-value-row">
              <div className="stat-card-value">{workspaceVisibility === null ? '—' : `${Math.round(workspaceVisibility)}%`}</div>
              {renderMetricTrend(visibilityTrend)}
            </div>
            <div className="remaining-range overview-visibility-range">
              <div
                className="range-filled overview-visibility-fill"
                style={{ width: `${Math.max(0, Math.min(100, workspaceVisibility ?? 0))}%` }}
              />
            </div>
          </div>
          <div className="stat-card-change is-neutral">
            {effectiveResponsesInRange.length > 0
              ? `Appeared in ${workspaceResponseIds.size} of ${effectiveResponsesInRange.length} searches`
              : 'No data yet'}
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <span className="stat-card-label">Avg Sentiment</span>
            <div className="stat-card-header-side">
              <InfoTooltip text="Sentiment is a score for how positively, negatively or neutrally AI platforms talk about your brand." />
            </div>
          </div>
          <div className="stat-card-value-row">
            <div className="stat-card-value">
              {formattedWorkspaceSentimentAverage}
            </div>
            {renderMetricTrend(sentimentTrend)}
          </div>
          <div className="stat-card-change is-neutral">{describeSentiment(workspaceSentimentAverage)}</div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <span className="stat-card-label">Avg Position</span>
            <div className="stat-card-header-side">
              <InfoTooltip text="Average ranking position for your brand when it appears in tracked prompt responses." />
            </div>
          </div>
          <div className="stat-card-value-row">
            <div className="stat-card-value">
              {workspaceAveragePosition === null ? '—' : workspaceAveragePosition.toFixed(1)}
            </div>
            {renderMetricTrend(positionTrend)}
          </div>
          <div className="stat-card-change is-neutral">
            {workspaceAveragePosition === null ? 'No ranking data yet' : 'Average brand ranking'}
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <span className="stat-card-label">Total Brand Mentions</span>
            <div className="stat-card-header-side">
              <InfoTooltip
                text="Total direct and indirect brand mentions found across tracked citations."
                preferredAlign="right"
              />
            </div>
          </div>
          <div className="stat-card-value-row">
            <div className="stat-card-value">{workspaceTotalBrandMentions.toLocaleString()}</div>
            {renderMetricTrend(mentionsTrend, 'right')}
          </div>
          <div className="stat-card-change is-neutral">Direct and indirect brand mentions</div>
        </div>
      </div>

      <div className="overview-top-grid">
        <div className="dashboard-panel overview-brand-visibility-panel">
          <div className="dashboard-panel-header">
            <div className="overview-panel-title-block">
              <div className="dashboard-panel-title">Brand Visibility</div>
              <div className="text-size-xsmall text-colour-alt-grey">
                See how many mentions each brand has
              </div>
            </div>
            <div className={`overview-chart-toggle overview-visibility-toggle ${brandChartMode === 'timeline' ? 'is-timeline' : 'is-brand'}`}>
              <button
                type="button"
                className={`overview-chart-toggle-button ${brandChartMode === 'brand' ? 'is-active' : ''}`}
                onClick={() => setBrandChartMode('brand')}
              >
                Default
              </button>
              <button
                type="button"
                className={`overview-chart-toggle-button ${brandChartMode === 'timeline' ? 'is-active' : ''}`}
                onClick={() => setBrandChartMode('timeline')}
              >
                Timeline
              </button>
            </div>
          </div>

          <div className="dashboard-panel-body">
            <div className="brand-visibility-chart-column">
              {brandChartMode === 'brand' && maxBrandVisibilityTotal === 0 ? (
                <div className="chart-placeholder overview-chart-placeholder">
                  <div style={{ textAlign: 'center' }}>
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--light-grey-outline)', marginBottom: '0.5rem' }}>
                      <line x1="12" x2="12" y1="20" y2="10" />
                      <line x1="18" x2="18" y1="20" y2="4" />
                      <line x1="6" x2="6" y1="20" y2="16" />
                    </svg>
                    <div className="text-size-small text-colour-light-grey">No mention data for this period</div>
                  </div>
                </div>
              ) : brandChartMode === 'timeline' && brandTimelineSeries.length === 0 ? (
                <div className="chart-placeholder overview-chart-placeholder">
                  <div style={{ textAlign: 'center' }}>
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--light-grey-outline)', marginBottom: '0.5rem' }}>
                      <path d="M3 3v18h18" />
                      <path d="m7 15 4-4 3 3 5-7" />
                    </svg>
                    <div className="text-size-small text-colour-light-grey">No mention timeline for this period</div>
                  </div>
                </div>
              ) : (
                <>
                  {brandChartMode === 'brand' ? (
                    <div className="brand-visibility-legend">
                      <span className="brand-visibility-legend-item info-tooltip-wrapper">
                        <div className="brand-visibility-legend-swatch is-direct"></div>
                        <span>Direct mentions</span>
                        <span className="info-tooltip">
                          Direct mentions are when an LLM directly cites the brand&apos;s own domain in its response.
                        </span>
                      </span>
                      <span className="brand-visibility-legend-item info-tooltip-wrapper">
                        <div className="brand-visibility-legend-swatch is-indirect"></div>
                        <span>Indirect mentions</span>
                        <span className="info-tooltip">
                          Indirect mentions are when an LLM cites another source that mentions the brand.
                        </span>
                      </span>
                    </div>
                  ) : (
                    <div className="brand-visibility-legend brand-timeline-legend">
                      {brandTimelineSeries.map((series) => (
                        <span
                          key={series.domain}
                          className={`brand-visibility-legend-item brand-timeline-legend-item ${
                            hiddenTimelineDomains.includes(series.domain) ? 'is-hidden' : ''
                          }`}
                          onClick={() => {
                            setHiddenTimelineDomains((current) =>
                              current.includes(series.domain)
                                ? current.filter((domain) => domain !== series.domain)
                                : [...current, series.domain]
                            );
                            if (activeTimelineDomain === series.domain) setActiveTimelineDomain(null);
                            if (hoveredTimelinePoint?.domain === series.domain) setHoveredTimelinePoint(null);
                          }}
                          onMouseEnter={() => setActiveTimelineDomain(series.domain)}
                          onMouseLeave={() => {
                            if (activeTimelineDomain === series.domain) setActiveTimelineDomain(null);
                          }}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              setHiddenTimelineDomains((current) =>
                                current.includes(series.domain)
                                  ? current.filter((domain) => domain !== series.domain)
                                  : [...current, series.domain]
                              );
                            }
                          }}
                          aria-pressed={!hiddenTimelineDomains.includes(series.domain)}
                          title={hiddenTimelineDomains.includes(series.domain) ? 'Show brand' : 'Hide brand'}
                        >
                          <div
                            className="brand-visibility-legend-swatch brand-timeline-legend-swatch"
                            style={{ backgroundColor: series.color }}
                          ></div>
                          <span>{series.name}</span>
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="brand-visibility-chart-shell">
                    <div className="brand-visibility-y-axis">
                      <div className="brand-visibility-y-axis-scale">
                        <span>{brandChartMode === 'brand' ? maxBrandVisibilityTotal : maxTimelineMentions}</span>
                        <span>{Math.round((brandChartMode === 'brand' ? maxBrandVisibilityTotal : maxTimelineMentions) / 2)}</span>
                        <span>0</span>
                      </div>
                      <div className="brand-visibility-y-axis-label">Total mentions</div>
                    </div>

                    <div className="brand-visibility-chart-frame">
                      {brandChartMode === 'brand' ? (
                        <>
                          <div className="brand-visibility-plot">
                            {brandVisibilityBars.map((bar) => {
                              const totalHeight = maxBrandVisibilityTotal > 0 ? (bar.total / maxBrandVisibilityTotal) * 100 : 0;
                              const directHeight = bar.total > 0 && bar.direct > 0
                                ? (bar.direct / bar.total) * totalHeight
                                : 0;
                              const indirectHeight = bar.total > 0 && bar.indirect > 0
                                ? (bar.indirect / bar.total) * totalHeight
                                : 0;

                              return (
                                <div key={bar.domain} className="visibility-bar-column">
                                  <div className="brand-visibility-tooltip">
                                    <div className="brand-visibility-tooltip-row">
                                      <span>Total</span>
                                      <span>{bar.total}</span>
                                    </div>
                                    <div className="brand-visibility-tooltip-row">
                                      <span>Direct</span>
                                      <span>{bar.direct}</span>
                                    </div>
                                    <div className="brand-visibility-tooltip-row">
                                      <span>Indirect</span>
                                      <span>{bar.indirect}</span>
                                    </div>
                                  </div>
                                  <div className="visibility-bar-track">
                                    {bar.total > 0 && (
                                      <>
                                        {indirectHeight > 0 && (
                                          <div
                                            className="visibility-bar-segment is-indirect"
                                            style={{ height: `${indirectHeight}%` }}
                                          />
                                        )}
                                        {directHeight > 0 && (
                                          <div
                                            className="visibility-bar-segment is-direct"
                                            style={{ height: `${directHeight}%` }}
                                          />
                                        )}
                                      </>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          <div className="brand-visibility-x-axis">
                            {brandVisibilityBars.map((bar) => (
                              <div key={bar.domain} className="visibility-bar-label">
                                {bar.name}
                              </div>
                            ))}
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="brand-visibility-plot brand-timeline-plot">
                            <svg
                              className="brand-timeline-svg"
                              viewBox={`0 0 ${TIMELINE_VIEWBOX_WIDTH} ${TIMELINE_VIEWBOX_HEIGHT}`}
                              preserveAspectRatio="none"
                            >
                              <defs>
                                {visibleTimelineSeries.map((series) => (
                                  <linearGradient
                                    key={`gradient-${series.domain}`}
                                    id={`timeline-gradient-${series.domain.replace(/[^a-z0-9_-]/gi, '-')}`}
                                    x1="0"
                                    y1="0"
                                    x2="0"
                                    y2="1"
                                  >
                                    <stop offset="0%" stopColor={series.color} stopOpacity="0.18" />
                                    <stop offset="75%" stopColor={series.color} stopOpacity="0.04" />
                                    <stop offset="100%" stopColor={series.color} stopOpacity="0" />
                                  </linearGradient>
                                ))}
                              </defs>
                              {visibleTimelineSeries.map((series) => {
                                const values = series.points.map((point) => point.total);
                                const gradientId = `timeline-gradient-${series.domain.replace(/[^a-z0-9_-]/gi, '-')}`;
                                const isDimmed = highlightedTimelineDomain !== null && highlightedTimelineDomain !== series.domain;

                                return (
                                  <g
                                    key={series.domain}
                                    className={`brand-timeline-series ${isDimmed ? 'is-dimmed' : ''}`}
                                    onMouseLeave={() => {
                                      if (activeTimelineDomain === series.domain) setActiveTimelineDomain(null);
                                      if (hoveredTimelinePoint?.domain === series.domain) setHoveredTimelinePoint(null);
                                    }}
                                  >
                                    <path
                                      d={buildTimelineAreaPath(values, maxTimelineMentions)}
                                      fill={`url(#${gradientId})`}
                                      className="brand-timeline-area"
                                    />
                                    <path
                                      d={buildTimelinePath(values, maxTimelineMentions)}
                                      fill="none"
                                      stroke="transparent"
                                      strokeWidth="14"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      className="brand-timeline-hover-target"
                                      onMouseEnter={() => setActiveTimelineDomain(series.domain)}
                                    />
                                    <path
                                      d={buildTimelinePath(values, maxTimelineMentions)}
                                      fill="none"
                                      stroke={series.color}
                                      strokeWidth="3"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      className="brand-timeline-line"
                                    />
                                  </g>
                                );
                              })}
                            </svg>
                            <div className="brand-timeline-points-layer">
                              {visibleTimelineSeries.map((series) => {
                                const isDimmed = highlightedTimelineDomain !== null && highlightedTimelineDomain !== series.domain;

                                return series.points.map((point, index) => {
                                  const x = getTimelinePointX(index, series.points.length);
                                  const y = getTimelinePointY(point.total, maxTimelineMentions);

                                  return (
                                    <button
                                      key={`${series.domain}-${point.key}`}
                                      type="button"
                                      className={`brand-timeline-point ${isDimmed ? 'is-dimmed' : ''}`}
                                      style={{
                                        left: `${(x / TIMELINE_VIEWBOX_WIDTH) * 100}%`,
                                        top: `${(y / TIMELINE_VIEWBOX_HEIGHT) * 100}%`,
                                        borderColor: series.color,
                                      }}
                                      onMouseEnter={() => {
                                        setActiveTimelineDomain(series.domain);
                                        setHoveredTimelinePoint({
                                          domain: series.domain,
                                          name: series.name,
                                          total: point.total,
                                          visibility: point.visibility,
                                          xPercent: (x / TIMELINE_VIEWBOX_WIDTH) * 100,
                                          yPercent: (y / TIMELINE_VIEWBOX_HEIGHT) * 100,
                                        });
                                      }}
                                      onMouseLeave={() => {
                                        setHoveredTimelinePoint(null);
                                      }}
                                      aria-label={`${series.name} on ${point.label}`}
                                    />
                                  );
                                });
                              })}
                            </div>
                            {hoveredTimelinePoint && (
                              <div
                                className="brand-visibility-tooltip brand-timeline-tooltip is-visible"
                                style={{
                                  left: `${hoveredTimelinePoint.xPercent}%`,
                                  top: `${hoveredTimelinePoint.yPercent}%`,
                                }}
                              >
                                <div className="brand-visibility-tooltip-row">
                                  <span>Brand</span>
                                  <span>{hoveredTimelinePoint.name}</span>
                                </div>
                                <div className="brand-visibility-tooltip-row">
                                  <span>Total mentions</span>
                                  <span>{hoveredTimelinePoint.total}</span>
                                </div>
                                <div className="brand-visibility-tooltip-row">
                                  <span>Visibility</span>
                                  <span>{hoveredTimelinePoint.visibility === null ? '—' : `${Math.round(hoveredTimelinePoint.visibility)}%`}</span>
                                </div>
                              </div>
                            )}
                          </div>

                          <div className="brand-visibility-x-axis brand-timeline-x-axis">
                            {timelineTickIndices.map((index) => {
                              const timelineBaseSeries = visibleTimelineSeries[0] ?? brandTimelineSeries[0];
                              const point = timelineBaseSeries?.points[index];
                              if (!point) return null;
                              const left = `${(getTimelinePointX(index, timelineBaseSeries.points.length) / TIMELINE_VIEWBOX_WIDTH) * 100}%`;

                              return (
                                <div key={point.key} className="brand-timeline-x-tick" style={{ left }}>
                                  {point.label}
                                </div>
                              );
                            })}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="dashboard-panel">
          <div className="dashboard-panel-header">
            <div className="overview-panel-title-block">
              <div className="table-title-wrapper is-left">
                <div className="dashboard-panel-title">Brand Mentions</div>
                <InfoTooltip text="Breakdown of total, direct, and indirect mentions for the selected brand." />
              </div>
              <div className="text-size-xsmall text-colour-alt-grey">
                See mentions for each brand you track
              </div>
            </div>
          </div>

          <div className="dashboard-panel-body">
            <div className="mention-overview-top">
              <div className="overview-brand-controls is-stacked" ref={brandDropdownRef}>
                <button
                  type="button"
                  className="date-range-toggle overview-brand-dropdown-toggle"
                  onClick={() => setIsBrandDropdownOpen((value) => !value)}
                >
                  <div className="overview-brand-dropdown-value">
                    {selectedBrand ? (
                      <>
                        <div
                          className="overview-brand-dropdown-icon"
                          style={{ backgroundImage: `url(${selectedBrand.faviconUrl})` }}
                        />
                        <div className="text-size-small text-weight-medium text-colour-dark-brown">
                          {selectedBrand.name}
                        </div>
                      </>
                    ) : (
                      <div className="text-size-small text-weight-medium text-colour-dark-brown">
                        Select brand
                      </div>
                    )}
                  </div>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={isBrandDropdownOpen ? { transform: 'rotate(180deg)', transition: 'transform 0.2s' } : { transition: 'transform 0.2s' }}>
                    <path d="m6 9 6 6 6-6" />
                  </svg>
                </button>

                {isBrandDropdownOpen && (
                  <div className="workspace-dropdown overview-brand-dropdown">
                    <div className="workspace-dropdown-list">
                      {brandEntities.map((brand) => (
                        <button
                          key={brand.domain}
                          type="button"
                          className={`workspace-dropdown-item ${brand.domain === selectedBrandDomain ? 'is-active' : ''}`}
                          onClick={() => {
                            setSelectedBrandDomain(brand.domain);
                            setIsBrandDropdownOpen(false);
                          }}
                        >
                          <div className="overview-brand-dropdown-item-main">
                            <div
                              className="overview-brand-dropdown-icon"
                              style={{ backgroundImage: `url(${brand.faviconUrl})` }}
                            />
                            <div>
                              <div className="text-size-small text-weight-medium text-colour-dark-brown">
                                {brand.name}
                              </div>
                              <div className="text-size-tiny text-colour-alt-grey">{brand.domain}</div>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="mention-overview-list">
              <div className="mention-overview-row">
                <div className="mention-overview-stat">
                  <div className="text-size-small text-colour-alt-grey">Total Brand Mentions</div>
                  <div className="mention-overview-value">
                    {selectedBrandCounts.total.toLocaleString()}
                  </div>
                </div>
                <Link
                  to={buildMentionsHref({ brandDomain: selectedBrandDomain })}
                  className="button is-tiny is-white has-inner-shadow w-button"
                >
                  View
                </Link>
              </div>

              <div className="mention-overview-row">
                <div className="mention-overview-stat">
                  <div className="overview-inline-label">
                    <div className="text-size-small text-colour-alt-grey">Direct Brand Mentions</div>
                    <InfoTooltip text="Direct mentions are when an LLM directly cites the brand's own domain in its response." />
                  </div>
                  <div className="mention-overview-value">{selectedBrandCounts.direct.toLocaleString()}</div>
                </div>
                <Link
                  to={buildMentionsHref({ brandDomain: selectedBrandDomain })}
                  className="button is-tiny is-white has-inner-shadow w-button"
                >
                  View
                </Link>
              </div>

              <div className="mention-overview-row">
                <div className="mention-overview-stat">
                  <div className="overview-inline-label">
                    <div className="text-size-small text-colour-alt-grey">Indirect Brand Mentions</div>
                    <InfoTooltip text="Indirect mentions are when an LLM cites another source that mentions the brand." />
                  </div>
                  <div className="mention-overview-value">{selectedBrandCounts.indirect.toLocaleString()}</div>
                </div>
                <Link
                  to={buildMentionsHref({ brandDomain: selectedBrandDomain })}
                  className="button is-tiny is-white has-inner-shadow w-button"
                >
                  View
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="dashboard-panel dashboard-panel-full">
          <div className="dashboard-panel-header">
            <div className="table-title-wrapper is-left">
              <div className="dashboard-panel-title">Competitor Ranking</div>
              <InfoTooltip text="How often your brand is mentioned compares to competitors across tracked prompt responses." />
            </div>
          </div>

          <div className="dashboard-panel-body is-table">
            <div className="table-scroll-wrap is-competitor-ranking-scroll">
            <table className="table-component competitor-ranking-table">
              <thead className="table-header">
                <tr className="table-row is-header-row">
                  <th className="table-head overview-rank-col">
                    <div className="text-size-small text-weight-semibold text-colour-alt-grey">#</div>
                  </th>
                  <th className="table-head is-name overview-brand-col">
                    <div className="text-size-small text-weight-semibold text-colour-alt-grey">Brand</div>
                  </th>
                  <th className="table-head overview-visibility-col">
                    <div className="text-size-small text-weight-semibold text-colour-alt-grey overview-table-head-label">
                      <span>Visibility</span>
                      <InfoTooltip text="Visibility shows how often this brand appears across tracked prompt searches in the selected date range." placement="bottom" />
                    </div>
                  </th>
                  <th className="table-head overview-mentions-col">
                    <div className="text-size-small text-weight-semibold text-colour-alt-grey">Total mentions</div>
                  </th>
                  <th className="table-head overview-sources-col">
                    <div className="text-size-small text-weight-semibold text-colour-alt-grey overview-table-head-label">
                      <span>Mention Sources</span>
                      <InfoTooltip text="Mention Sources shows the main websites or LLMs contributing mentions for this brand in the selected date range." placement="bottom" />
                    </div>
                  </th>
                  <th className="table-head overview-action-col">
                    <div className="text-size-small text-weight-semibold text-colour-alt-grey" aria-hidden="true">&nbsp;</div>
                  </th>
                </tr>
              </thead>
              <tbody className="table-body">
                {rankingRows.length === 0 ? (
                  <tr className="table-row">
                    <td className="table-cell" colSpan={6}>
                      <div className="overview-empty-row">
                        Add competitors via the <Link to="/competitors" className="link-style">Competitors</Link> page
                      </div>
                    </td>
                  </tr>
                ) : (
                  rankingRows.slice(0, 6).map((row, index) => (
                    <tr key={row.domain} className="table-row">
                      <td className="table-cell overview-rank-col">
                        <div className="text-size-small text-colour-alt-grey">{index + 1}</div>
                      </td>
                      <td className="table-cell is-name overview-brand-col">
                        <div className="ranking-brand-cell">
                          <div
                            className="ranking-brand-icon"
                            style={
                              row.faviconUrl
                                ? { backgroundImage: `url(${row.faviconUrl})` }
                                : { background: BRAND_COLORS[index % BRAND_COLORS.length] }
                            }
                          >
                            {!row.faviconUrl && row.name[0]}
                          </div>
                          <div>
                            <div className="ranking-brand-title">
                              <div className="text-size-small text-weight-semibold">{row.name}</div>
                              {row.isWorkspaceBrand && <div className="new-span is-ranking-you">You</div>}
                            </div>
                            <div className="text-size-tiny text-colour-alt-grey">{row.domain}</div>
                          </div>
                        </div>
                      </td>
                      <td className="table-cell overview-visibility-col">
                        {row.visibility === null ? (
                          <div className="text-size-small text-colour-alt-grey">—</div>
                        ) : (
                          <RankingVisibilityTooltip
                            text={`This brand shows up in ${row.visibleResponses} of ${row.totalResponses} prompt searches, resulting in ${Math.round(row.visibility)}% visibility.`}
                          >
                            <div className="overview-ranking-visibility">
                              <div className="remaining-range overview-ranking-visibility-range">
                                <div
                                  className="range-filled overview-ranking-visibility-fill"
                                  style={{ width: `${Math.max(0, Math.min(100, row.visibility))}%` }}
                                />
                              </div>
                              <div className="text-size-small text-colour-alt-grey">
                                {Math.round(row.visibility)}%
                              </div>
                            </div>
                          </RankingVisibilityTooltip>
                        )}
                      </td>
                      <td className="table-cell overview-mentions-col">
                        <div className={`table-count-badge ${row.totalMentions === 0 ? 'is-zero' : ''}`}>{row.totalMentions.toLocaleString()}</div>
                      </td>
                      <td className="table-cell overview-sources-col">
                        {row.mentionSources.length === 0 ? (
                          <div className="text-size-small text-colour-light-grey">—</div>
                        ) : (
                          <div className="overview-source-chip-list">
                            {row.mentionSources.map((source) => (
                              <span key={source.key} className="overview-source-chip info-tooltip-wrapper">
                                <span className={`overview-source-chip-icon ${source.kind === 'llm' ? 'is-llm' : 'is-site'}`}>
                                  {source.kind === 'llm' ? (
                                    source.icon ? (
                                      <img src={source.icon} alt={source.label} className="overview-source-icon-image" />
                                    ) : (
                                      source.shortLabel
                                    )
                                  ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                      <circle cx="12" cy="12" r="10" />
                                      <path d="M2 12h20" />
                                      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10Z" />
                                    </svg>
                                  )}
                                </span>
                                <span className="overview-source-chip-count">{source.count}</span>
                                <span className="info-tooltip">
                                  {source.label}: {source.count} mention{source.count === 1 ? '' : 's'}
                                </span>
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="table-cell overview-action-col">
                        <Link
                          to={buildMentionsHref({ brandDomain: row.domain })}
                          className="button is-tiny is-white has-inner-shadow w-button"
                        >
                          View
                        </Link>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            </div>
          </div>
        </div>

      <div className="spacer-tiny"></div>

      <div className="overview-section-header">
        <div className="overview-header-left">
          <div className="table-title-wrapper is-left">
            <div className="text-size-large text-weight-semibold text-colour-dark-brown">Recent Prompt Searches</div>
            <InfoTooltip text="The most recent prompt runs in the selected date range." />
          </div>
        </div>
        <Link to="/searches" className="button is-small is-white has-inner-shadow w-button">
          See all searches
        </Link>
      </div>

      {recentPromptSearches.length === 0 ? (
        <div className="dashboard-panel dashboard-panel-full">
          <div className="dashboard-panel-body">
            <div className="overview-panel-empty">
              No prompt searches found for this period
            </div>
          </div>
        </div>
      ) : (
        <div className="recent-search-grid">
          {recentPromptSearches.map((item) => {
            const modelMeta = getLlmMeta(item.model);

            return (
              <div key={item.id} className="recent-search-card">
                <div className="recent-search-card-main">
                  <div className="recent-search-prompt">{item.promptText}</div>
                  <div className="recent-search-meta">
                    <div className="recent-search-model">
                      <div className="overview-model-badge">
                        {modelMeta.icon ? (
                          <img src={modelMeta.icon} alt={modelMeta.name} className="overview-model-badge-image" />
                        ) : (
                          modelMeta.short
                        )}
                      </div>
                      <span>{modelMeta.name}</span>
                    </div>
                    <div className="recent-search-date">{formatDate(item.createdAt)}</div>
                  </div>
                </div>

                <button
                  type="button"
                  className="button is-tiny is-white has-inner-shadow w-button"
                  onClick={() => setSelectedRecentSearchId(item.id)}
                >
                  See full answer
                </button>
              </div>
            );
          })}
        </div>
      )}

      <SearchResponseModal item={selectedRecentSearch} onClose={() => setSelectedRecentSearchId(null)} />

      <div className="spacer-large"></div>
    </div>
  );
}
